/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../../store/userSlice";
// actions
import { addInstance, updateInstance, deleteInstance } from "../../../actions";
import { getUsers } from "../../users/actions";
import authRoles from "../../../auth/authRoles";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
// custom components
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";
import ProjectStatusSteps from "../listing/components/ProjectStatusSteps";
import DeleteModal from "../../../shared-components/crud/DeleteModal";

// form validation
const schema = yup.object().shape({
  title: yup.string().required("You must enter a title"),
  label: yup.string().required("You must enter a label"),
  classification: yup.string().required("You must enter a classification"),
  progress: yup.object().shape({
    development: yup.number().required("You must enter a storyboard progress"),
    production: yup.number().required("You must enter an IMI progress"),
  }),
  manager: yup.string().required("You must select a program manager"),
  client: yup.string().required("You must select a client"),
  cor: yup.string().required("You must select a COR"),
});

const ProjectForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();
  const [users, setUsers] = useState(null);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  // form methods
  const { control, watch, handleSubmit, reset, formState, getValues } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // get user listing
  const handleGetUsers = async () => {
    try {
      const response = await dispatch(getUsers()).unwrap();
      setUsers(response);
    } catch (err) {
      console.log(err.message);
    }
  }
  // ensure users are only fetched once
  useEffect(() => {
    // get users
    handleGetUsers();
  }, [dispatch, routeParams]);
  // form submisson
  function onSubmit(data) {
    // add or update the project instance
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('projects')(data), 'post');
    } else {
      handleInstanceAction(updateInstance('projects')(data), 'patch');
    }
  }
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // handle removal of project
  const handleRemoveProject = () => {
    // build request data
    const data = {
      id: instance.id,
      data: {
        user: user._id || user.id
      }
    };
    handleInstanceAction(deleteInstance('projects')(data), 'delete');
    // navigate to projects listing
    navigate('/projects');
  }
  // on instance change, reset the form
  useEffect(() => {
    let data = {
      ...instance,
      user: user._id,
      manager: instance.manager.id || "",
      client: instance.client.id || "",
      cor: instance.cor.id || "",
    };
    reset({ ...data });
  }, [instance, reset]);
  // if data is missing, return loading
  if (_.isEmpty(form) || !instance || !users) {
    return <BASELoading />;
  }

  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="Basic Information" description="Enter the basic project details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  placeholder="Title"
                  id="title"
                  error={!!errors.title}
                  helperText={errors?.title?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="label"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Label"
                  placeholder="Label"
                  id="label"
                  error={!!errors.label}
                  helperText={errors?.label?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="classification"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Classification"
                  placeholder="Classification"
                  id="label"
                  error={!!errors.classification}
                  helperText={errors?.classification?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Points of Contact" description="Select personnel for each point of contact." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.manager}
              required
            >
              <InputLabel id="manager">Program Manager</InputLabel>
              <Controller
                control={control}
                name="manager"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Program Manager"
                    placeholder="Program Manager"
                    id="manager"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Manager</MenuItem>
                    {users.map((user) => (
                      <MenuItem value={user.id} key={user.id} className="space-x-12">
                        <span>{user.first_name} {user.last_name}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-1">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.client}
              required
            >
              <InputLabel id="client">Client</InputLabel>
              <Controller
                control={control}
                name="client"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Client"
                    placeholder="Client"
                    id="client"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Client</MenuItem>
                    {users.map((user) => (
                      <MenuItem value={user.id} key={user.id} className="space-x-12">
                        <span>{user.first_name} {user.last_name}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-1">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.cor}
              required
            >
              <InputLabel id="cor">COR</InputLabel>
              <Controller
                control={control}
                name="cor"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="COR"
                    placeholder="COR"
                    id="cor"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a COR</MenuItem>
                    {users.map((user) => (
                      <MenuItem value={user.id} key={user.id} className="space-x-12">
                        <span>{user.first_name} {user.last_name}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div>

        <Divider className="my-40" />
        
        <FormSectionHeader title="Project Status" description="These values are here for progress and status." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Progress:</Typography>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.development"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Storyboard Progress"
                  placeholder="Storyboard Progress"
                  id="progress_development"
                  error={!!errors?.progress?.development}
                  helperText={errors?.progress?.development?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.production"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="IMI Progress"
                  placeholder="IMI Progress"
                  id="progress_production"
                  error={!!errors?.progress?.production}
                  helperText={errors?.progress?.production?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Status:</Typography>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.development}
              required
            >
              <InputLabel id="status_development">Storyboard Status</InputLabel>
              <Controller
                control={control}
                name="status.development"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_development"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(ProjectStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{ProjectStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.production}
              required
            >
              <InputLabel id="status_production">IMI Status</InputLabel>
              <Controller
                control={control}
                name="status.production"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_production"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(ProjectStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{ProjectStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div>
      </div>
      <Box
        className="flex items-center mt-40 py-14 px-24 sm:px-32 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.id !== 'new' && (
          authRoles.admin.includes(user.role) && (
            <DeleteModal onDelete={handleRemoveProject} resource="project" />
          )
        )}
        <Button
          component={Link}
          color="primary"
          variant="outlined"
          className="ml-auto"
          to="/projects"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div>
  );
};

export default ProjectForm;
